import { useState, useEffect, useRef } from "react";
import { ImagePlus, Lock } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, BACKEND_URL } from "@/lib/api";

const EFFECT_OPTIONS = ["", "golden_sweep", "diamond_shatter", "royal_cinematic", "neon_storm", "fire_wave", "butterfly_bloom", "galaxy_warp"];
const PERKS = [
  ["colored_name", "Colored name in chat"],
  ["animated_badge", "Animated badge"],
  ["entry_message", "Entry message"],
  ["premium_entry", "Premium entry animation"],
  ["name_glow", "Name glow"],
  ["exclusive_gifts", "Exclusive gifts unlocked"],
  ["vip_seat", "VIP seat (pinned avatar)"],
  ["priority_chat", "Priority chat"],
  ["cinematic_entry", "Cinematic entry"],
  ["platform_announce_levelup", "Platform-wide level-up announce"],
  ["royal_frame", "Royal frame on avatar"],
];

function TierCard({ tier, onSaved }) {
  const [t, setT] = useState(tier);
  const [busy, setBusy] = useState(false);
  const iconRef = useRef(null);
  const set = (k, v) => setT((x) => ({ ...x, [k]: v }));
  const setPerk = (k, v) => setT((x) => ({ ...x, perks: { ...x.perks, [k]: v } }));

  const uploadIcon = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("icon", file);
      const { data } = await adminApi.post(`/admin/tiers/${t.id}/icon`, fd);
      set("icon_url", data.icon_url);
      toast.success(`${t.name} icon uploaded — now shows next to names in chat`);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/tiers/${t.id}`, {
        price_inr: t.invitation_only ? undefined : Number(t.price_inr),
        price_coins: t.invitation_only ? undefined : Number(t.price_coins),
        color: t.color, emoji: t.emoji, entry_effect: t.entry_effect || "", perks: t.perks,
      });
      toast.success(`${t.name} level saved — live instantly`);
      onSaved();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid={`tier-card-${tier.id}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-zinc-600 text-xs font-extrabold w-5">{t.rank}</span>
        <input ref={iconRef} type="file" accept="image/*" className="hidden" onChange={uploadIcon} data-testid={`tier-icon-input-${tier.id}`} />
        <button type="button" data-testid={`tier-icon-upload-${tier.id}`} onClick={() => iconRef.current?.click()}
          title="Upload level icon (shows in chat badges)"
          className="w-10 h-10 rounded-xl bg-[#1A1A24] border border-white/10 flex items-center justify-center shrink-0 hover:border-violet-500 transition-colors">
          {t.icon_url ? (
            <img src={`${BACKEND_URL}${t.icon_url}`} alt="" className="w-7 h-7 object-contain" data-testid={`tier-icon-preview-${tier.id}`} />
          ) : (
            <span className="text-xl">{t.emoji || <ImagePlus size={16} className="text-zinc-500" />}</span>
          )}
        </button>
        <div className="min-w-0">
          <p className="text-sm font-extrabold" style={{ color: t.color }}>{t.name}</p>
          <p className="text-zinc-500 text-[10px] truncate">{t.meaning}</p>
        </div>
        <input data-testid={`tier-color-${tier.id}`} type="color" value={t.color || "#7C3AED"} onChange={(e) => set("color", e.target.value)}
          className="w-9 h-9 rounded-lg bg-transparent border border-white/10 cursor-pointer ml-auto shrink-0" />
        <input data-testid={`tier-emoji-${tier.id}`} value={t.emoji || ""} onChange={(e) => set("emoji", e.target.value)} placeholder="emoji"
          className="bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1.5 text-sm outline-none w-14 text-center shrink-0" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs">
        {t.invitation_only ? (
          <span className="text-[10px] text-amber-400 font-bold flex items-center gap-1.5 bg-amber-500/10 px-3 py-1.5 rounded-full"><Lock size={11} /> Invitation only — grant from Users page</span>
        ) : (
          <>
            <label className="text-zinc-500 font-semibold">₹ Price</label>
            <input data-testid={`tier-price-inr-${tier.id}`} type="number" min={0} value={t.price_inr ?? 0} onChange={(e) => set("price_inr", e.target.value)}
              className="w-24 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1.5 outline-none" />
            <label className="text-zinc-500 font-semibold">Coins</label>
            <input data-testid={`tier-price-coins-${tier.id}`} type="number" min={0} value={t.price_coins ?? 0} onChange={(e) => set("price_coins", e.target.value)}
              className="w-28 bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1.5 outline-none" />
          </>
        )}
        <label className="text-zinc-500 font-semibold ml-auto">Entry effect</label>
        <select data-testid={`tier-effect-${tier.id}`} value={t.entry_effect || ""} onChange={(e) => set("entry_effect", e.target.value)}
          className="bg-[#1A1A24] border border-white/10 rounded-lg px-2 py-1.5 outline-none">
          {EFFECT_OPTIONS.map((o) => <option key={o} value={o}>{o || "none"}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5 mb-4">
        {PERKS.map(([k, label]) => (
          <label key={k} className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer">
            <input data-testid={`tier-perk-${k}-${tier.id}`} type="checkbox" checked={!!t.perks?.[k]} onChange={(e) => setPerk(k, e.target.checked)} className="accent-pink-500" />
            {label}
          </label>
        ))}
      </div>
      <button data-testid={`tier-save-${tier.id}`} onClick={save} disabled={busy}
        className="gradient-primary text-white text-xs font-bold px-5 py-2 rounded-full disabled:opacity-40">
        {busy ? "Saving…" : "Save Level"}
      </button>
    </div>
  );
}

export default function AdminTiers() {
  const [tiers, setTiers] = useState(null);
  const [curve, setCurve] = useState({ xp_divisor: 50, xp_exponent: 0.55 });

  const load = () => adminApi.get("/admin/tiers").then(({ data }) => {
    setTiers(data.tiers);
    setCurve(data.xp_curve);
  }).catch(() => setTiers([]));

  useEffect(() => { load(); }, []);

  const saveCurve = async () => {
    try {
      await adminApi.put("/admin/settings/xp-curve", { xp_divisor: Number(curve.xp_divisor), xp_exponent: Number(curve.xp_exponent) });
      toast.success("XP curve updated");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div data-testid="admin-tiers">
      <h1 className="text-xl font-extrabold font-display mb-2">Navaratna Ladder Manager</h1>
      <p className="text-zinc-500 text-xs mb-6">Paid 30-day loyalty levels (Friendship → Shahenshah). Users buy levels with coins; top levels are invitation-only (grant from the Users page). Changes apply instantly.</p>

      <div className="glass rounded-2xl p-5 mb-6">
        <p className="text-sm font-bold mb-3">XP Curve (activity levels Lv.1–100, separate from the ladder) — level = (xp ÷ divisor) ^ exponent + 1</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] text-zinc-500 font-semibold block mb-1">Divisor</label>
            <input data-testid="xp-divisor-input" type="number" value={curve.xp_divisor} onChange={(e) => setCurve((c) => ({ ...c, xp_divisor: e.target.value }))}
              className="w-24 bg-[#1A1A24] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 font-semibold block mb-1">Exponent</label>
            <input data-testid="xp-exponent-input" type="number" step="0.01" value={curve.xp_exponent} onChange={(e) => setCurve((c) => ({ ...c, xp_exponent: e.target.value }))}
              className="w-24 bg-[#1A1A24] border border-white/10 rounded-lg px-2.5 py-2 text-sm outline-none" />
          </div>
          <button data-testid="xp-curve-save" onClick={saveCurve} className="gradient-primary text-white text-xs font-bold px-5 py-2.5 rounded-full">Save Curve</button>
          <div className="flex flex-wrap gap-2 text-[10px] ml-auto max-w-md justify-end">
            {tiers?.map((t) => (
              <span key={t.id} style={{ color: t.color }} className="font-bold">{t.emoji} {t.name}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {tiers === null && Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-48 rounded-2xl" />)}
        {tiers?.map((t) => <TierCard key={t.id} tier={t} onSaved={load} />)}
      </div>
    </div>
  );
}
